/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable no-param-reassign */
/* eslint-disable react/prop-types */
/* eslint-disable no-case-declarations */
import React, { useEffect, useState, useLayoutEffect, useRef } from "react";
import axios from "axios";
import { Bookmark } from "lucide-react";
import { Worker, Viewer, RotateDirection } from "@react-pdf-viewer/core";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { PDFDocument, degrees } from "pdf-lib";
import {
  FirstPageIcon,
  LastPageIcon,
  NextPageIcon,
  PrevPageIcon,
  RefreshIcon,
  ZoomInIcon,
  ZoomOutIcon,
  DownloadIcon,
  RedirectIcon,
  SplitscreenViewIcon,
  RotateClockwiseIcon
} from "./DocumentIcons";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { findMinMax } from "./constants";
import { TTFReviewCacheProvider } from "./TTFReviewCacheProvider";
import { CircularProgressWithLabel } from "./CircularProgressWithLabel";
import PropTypes from "prop-types";

const NavigationButtons = ({ firstPageProps, prevPageProps, nextPageProps, lastPageProps, CurrentPageInput, NumberOfPages }) => (
  <>
    <div className="px-0.5">
      <Button
        size="icon"
        variant="ghost"
        aria-label='first-page'
        data-testid='first-page'
        onClick={firstPageProps.onClick}
        disabled={firstPageProps.isDisabled}
      >
        <FirstPageIcon size='18' />
      </Button>
    </div>
    <div className="px-0.5">
      <Button
        size="icon"
        variant="ghost"
        aria-label='previous-page'
        data-testid='previous-page'
        onClick={prevPageProps.onClick}
        disabled={prevPageProps.isDisabled}
      >
        <PrevPageIcon size='18' />
      </Button>
    </div>
    <div className="px-0.5 w-[110px] flex items-center [&_input]:pl-1.5">
      <CurrentPageInput /> / <NumberOfPages />
    </div>
    <div className="px-0.5">
      <Button
        size="icon"
        variant="ghost"
        aria-label='next-page'
        data-testid='next-page'
        onClick={nextPageProps.onClick}
        disabled={nextPageProps.isDisabled}
      >
        <NextPageIcon size='18' />
      </Button>
    </div>
    <div className="px-0.5">
      <Button
        size="icon"
        variant="ghost"
        aria-label='last-page'
        data-testid='last-page'
        onClick={lastPageProps.onClick}
        disabled={lastPageProps.isDisabled}
      >
        <LastPageIcon size='18' />
      </Button>
    </div>
  </>
);

const ZoomButtons = ({ zoomProps, zoomOutProps, zoomInProps, Zoom }) => (
  <>
    <div className="px-0.5">
      <Button
        size="icon"
        variant="ghost"
        aria-label='zoom-out'
        data-testid='zoom-out'
        onClick={zoomOutProps.onClick}
        disabled={zoomProps.scale === 0.1}
      >
        <ZoomOutIcon size='18' />
      </Button>
    </div>
    <div className="px-0.5">
      <Zoom />
    </div>
    <div className="px-0.5">
      <Button
        size="icon"
        variant="ghost"
        aria-label='zoom-in'
        data-testid='zoom-in'
        onClick={zoomInProps.onClick}
        disabled={zoomProps.scale === 10}
      >
        <ZoomInIcon size='18' />
      </Button>
    </div>
  </>
);

const FirstPrevWrapper = ({ GoToFirstPage, GoToPreviousPage, children }) => (
  <GoToFirstPage>
    {(firstPageProps) => (
      <GoToPreviousPage>
        {(prevPageProps) => children({ firstPageProps, prevPageProps })}
      </GoToPreviousPage>
    )}
  </GoToFirstPage>
);

const NextLastWrapper = ({ GoToNextPage, GoToLastPage, children }) => (
  <GoToNextPage>
    {(nextPageProps) => (
      <GoToLastPage>
        {(lastPageProps) => children({ nextPageProps, lastPageProps })}
      </GoToLastPage>
    )}
  </GoToNextPage>
);

const NavigationWrapper = ({ GoToFirstPage, GoToPreviousPage, GoToNextPage, GoToLastPage, CurrentPageInput, NumberOfPages }) => (
  <FirstPrevWrapper GoToFirstPage={GoToFirstPage} GoToPreviousPage={GoToPreviousPage}>
    {({ firstPageProps, prevPageProps }) => (
      <NextLastWrapper GoToNextPage={GoToNextPage} GoToLastPage={GoToLastPage}>
        {({ nextPageProps, lastPageProps }) => (
          <NavigationButtons
            firstPageProps={firstPageProps}
            prevPageProps={prevPageProps}
            nextPageProps={nextPageProps}
            lastPageProps={lastPageProps}
            CurrentPageInput={CurrentPageInput}
            NumberOfPages={NumberOfPages}
          />
        )}
      </NextLastWrapper>
    )}
  </FirstPrevWrapper>
);

const ZoomWrapper = ({ Zoom, ZoomOut, ZoomIn }) => (
  <Zoom>
    {(zoomProps) => (
      <ZoomOut>
        {(zoomOutProps) => (
          <ZoomIn>
            {(zoomInProps) => (
              <ZoomButtons
                zoomProps={zoomProps}
                zoomOutProps={zoomOutProps}
                zoomInProps={zoomInProps}
                Zoom={Zoom}
              />
            )}
          </ZoomIn>
        )}
      </ZoomOut>
    )}
  </Zoom>
);

const getBoundingRectForAngle = {
  90: (item, canvasWidth, canvasHeight) => ({
    left: canvasWidth - item.bottomRight[1] * canvasWidth,
    top: item.topLeft[0] * canvasHeight,
    width: (item.bottomRight[1] - item.topLeft[1]) * canvasWidth,
    height: (item.bottomRight[0] - item.topLeft[0]) * canvasHeight
  }),
  180: (item, canvasWidth, canvasHeight) => ({
    left: canvasWidth - item.bottomRight[0] * canvasWidth,
    top: canvasHeight - item.bottomRight[1] * canvasHeight,
    width: (item.bottomRight[0] - item.topLeft[0]) * canvasWidth,
    height: (item.bottomRight[1] - item.topLeft[1]) * canvasHeight
  }),
  270: (item, canvasWidth, canvasHeight) => ({
    left: item.topLeft[1] * canvasWidth,
    top: canvasHeight - item.bottomRight[0] * canvasHeight,
    width: (item.bottomRight[1] - item.topLeft[1]) * canvasWidth,
    height: (item.bottomRight[0] - item.topLeft[0]) * canvasHeight
  }),
  0: (item, canvasWidth, canvasHeight) => ({
    left: item.topLeft[0] * canvasWidth,
    top: item.topLeft[1] * canvasHeight,
    width: (item.bottomRight[0] - item.topLeft[0]) * canvasWidth,
    height: (item.bottomRight[1] - item.topLeft[1]) * canvasHeight
  })
};

const calculateBoundingRect = (item, canvasWidth, canvasHeight, angle) => {
  const calculator = getBoundingRectForAngle[angle] || getBoundingRectForAngle[0];
  return calculator(item, canvasWidth, canvasHeight);
};

export const PDFViewer = (props) => {
  const cacheProvider = TTFReviewCacheProvider();
  const pixelPadding = 7;
  const [PDFData, setPDFData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [currentAngle, setCurrentAngle] = useState(0);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [redownload, setRedownload] = useState(false);
  const [isRotated, setIsRotated] = useState(false);

  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { jumpToPage } = pageNavigationPluginInstance;
  const {
    state,
    setState,
    cacheKey,
    url,
    useCache = true,
    name,
    highlightChecked = true,
    showHighlight = true,
    highlightDisabled,
    onChangeHighlightSelect,
    theme,
    //getDocumentFromServer,
    reuploadFile,
    captureDocNotReq
  } = props;

  const [isDocModified, setIsDocModified] = useState(false);
  const [downloadedDocRes, setDownloadedDocRes] = useState([]);
  const { activePageNumber, highlightCoordinates = [] } = state;
  const didMount = useRef(false);

  useEffect(() => {
    return () => {
      if (isDocModified) {
        cacheProvider.removeItem(cacheKey.toString());
      }
    };
  }, [isDocModified]);

  const onClickOpenInNewTab = () => {
    const fullUrl = `/document-viewer/${cacheKey}`;
    window.open(fullUrl, "_blank");
  };

  useLayoutEffect(() => {
    if (activePageNumber) {
      jumpToPage(activePageNumber - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePageNumber]);

  const onClickDownload = (downloadUrl) => {
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", name); // any other extension
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const rotatePdf = async (angle) => {
    const originalPdfBytes = await downloadedDocRes.arrayBuffer();
    const pdfDoc = await PDFDocument.load(originalPdfBytes, {
      ignoreEncryption: true
    });
    const pages = pdfDoc.getPages();

    pages.forEach((page) => {
      const rotation = page.getRotation().angle;
      page.setRotation(degrees(rotation + angle));
    });

    const rotatedPdfBytes = await pdfDoc.save();
    setIsDocModified(true);
    return new Blob([rotatedPdfBytes], { type: "application/pdf" });
  };

  const captureAndRotatePdf = async (angle) => {
    const rotatedPfdfBlob = await rotatePdf(angle);
    const formData = new FormData();
    formData.append("file", rotatedPfdfBlob, name);
    let filePath = "";
    const index = state.path.indexOf("loan");
    if (index !== -1) {
      // Extract the part of the URL from the keyword onward
      filePath = state.path.slice(index);
    }
    await reuploadFile({ filePath, documentId: state.documentId, file: formData });
    setIsRotated(false);
  };
  const handleRotate = async (item) => {
    item.onClick();
    setIsRotated(true);
    setCurrentAngle((angleCurrent) => {
      return (angleCurrent + 90) % 360;
    });
  };
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
    }
    if (isRotated && !captureDocNotReq) {
      captureAndRotatePdf(currentAngle);
    }
    if (captureDocNotReq){
      setIsRotated(false);
    }
  }, [currentAngle, isRotated]);

  const RotateButton = ({ Rotate }) => (
    <div className="px-0.5 pt-0.5">
      <Rotate direction={RotateDirection.Forward}>
        {(direction) => (
          <Button
            size="icon"
            variant="ghost"
            aria-label='rotate-clockwise'
            data-testid='rotate-clockwise'
            onClick={() => handleRotate(direction)}
            disabled={isRotated}
          >
            <RotateClockwiseIcon viewBox='0 3 16 16' />
          </Button>
        )}
      </Rotate>
    </div>
  );

  const HighlightCheckbox = () => (
    <div className="px-2 relative -top-0.5">
      <Checkbox
        id="highlight-checkbox"
        checked={highlightChecked}
        onCheckedChange={onChangeHighlightSelect}
        disabled={highlightDisabled}
        label="Highlight"
        labelClassName="text-xs font-normal"
      />
    </div>
  );

  const OpenInNewTabButton = () => (
    <div className="px-0.5">
      <Button
        size="icon"
        variant="ghost"
        aria-label='open-in-new-tab'
        data-testid='open-in-new-tab'
        onClick={onClickOpenInNewTab}
      >
        <RedirectIcon color='#183238' width={20} height={20} />
      </Button>
    </div>
  );

  const DownloadButton = () => (
    <div className="px-0.5 mr-1.5">
      <Button
        size="icon"
        variant="ghost"
        aria-label='download'
        data-testid='download'
        onClick={() => onClickDownload(PDFData || "")}
      >
        <DownloadIcon size='21' viewBox='0 1 16 16' />
      </Button>
    </div>
  );

  const renderToolbar = (Toolbar) => (
    <Toolbar>
      {(slots) => {
        const {
          CurrentPageInput,
          Rotate,
          GoToFirstPage,
          GoToLastPage,
          GoToNextPage,
          GoToPreviousPage,
          NumberOfPages,
          ShowSearchPopover,
          Zoom,
          ZoomIn,
          ZoomOut
        } = slots;
        return (
          <div className="flex items-center w-full">
            <div className="px-0.5 ml-1 [&_svg]:transform [&_svg]:translate-y-1">
              <ShowSearchPopover />
            </div>
            <NavigationWrapper
              GoToFirstPage={GoToFirstPage}
              GoToPreviousPage={GoToPreviousPage}
              GoToNextPage={GoToNextPage}
              GoToLastPage={GoToLastPage}
              CurrentPageInput={CurrentPageInput}
              NumberOfPages={NumberOfPages}
            />
            <ZoomWrapper Zoom={Zoom} ZoomOut={ZoomOut} ZoomIn={ZoomIn} />
            <RotateButton Rotate={Rotate} />
            {showHighlight && <HighlightCheckbox />}
            {useCache && <OpenInNewTabButton />}
            <DownloadButton />
          </div>
        );
      }}
    </Toolbar>
  );

  const sidebarTabs = (defaultTabs) => {
    defaultTabs[0].icon = <SplitscreenViewIcon />;
    defaultTabs[1].icon = (
      <Bookmark
        className="w-3.5 h-4"
        style={{ viewBox: '3 0 18 18' }}
      />
    );
    return [defaultTabs[0], defaultTabs[1]];
  };

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar,
    sidebarTabs
  });

  const downloadFromServer = async () => {
    const controller = new AbortController();
    setCurrentRequest(controller);
    let downloadURL = url;
    if (!downloadURL && cacheKey) {
      const responseData = await url;
      downloadURL = responseData?.data?.url;
    }
    if (!downloadURL) return null;
    const response = await axios({
      url: downloadURL,
      signal: controller.signal,
      responseType: "blob",
      onDownloadProgress(progressEvent) {
        const progress = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100
        );
        setDownloadProgress(progress);
      }
    });
    const { data } = response;
    setDownloadedDocRes(data);
    if (cacheKey) {
      cacheProvider.setItem(cacheKey, data);
    }
    return data;
  };

  const readFile = async (useCacheLocal = false) => {
    try {
      const data =
        (useCacheLocal &&
          cacheKey &&
          (await cacheProvider.getItem(cacheKey))) ||
        (await downloadFromServer());
      if (url) {
        setPDFData(url);
      } else {
        setPDFData(window.URL.createObjectURL(data));
      }
    } catch {
      console.error(`Error reading data from cache`);
    }
  };

  const triggerRender = () => {
    if (currentRequest !== null) {
      currentRequest.abort();
      setCurrentRequest(null);
    }
    setPDFData(null);
    const updatedObj = JSON.parse(JSON.stringify(state));
    updatedObj.activePageNumber = 0;
    updatedObj.isDocumentLoading = true;
    if (setState) {
      setState(updatedObj);
    }

    setDownloadProgress(null);
    readFile(useCache);
  };

  useEffect(() => {
    setCurrentAngle(0);
  }, [cacheKey]);

  useEffect(() => {
    triggerRender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, redownload, url]);

  const onDocumentLoadSuccess = (e) => {
    const updatedObj = JSON.parse(JSON.stringify(state));
    updatedObj.totalPages = e?.doc?.numPages;
    updatedObj.activePageNumber = highlightCoordinates.length
      ? findMinMax(highlightCoordinates, "pageNo").min
      : 1;
    updatedObj.isDocumentLoading = false;
    if (setState) {
      setState(updatedObj);
    }
  };

  const getBoundingRectCoordinates = (item, canvasWidth, canvasHeight) => {
    return calculateBoundingRect(item, canvasWidth, canvasHeight, currentAngle);
  };

  const onRenderPage = (props1) => {
    const highlightSpans = [];
    highlightCoordinates.forEach((item) => {
      if ((props1.pageIndex + 1).toString() === item?.pageNo?.toString()) {
        const { left, top, width, height } = getBoundingRectCoordinates(
          item,
          props1.width,
          props1.height
        );
        highlightSpans.push(
          <span
            style={{
              position: "absolute",
              left: left - pixelPadding,
              width: width + 2 * pixelPadding,
              height: height + 2 * pixelPadding,
              top: top - pixelPadding,
              border: "2px solid red"
            }}
          />
        );
      }
    });
    return (
      <>
        {props1.canvasLayer.children}
        <div
          style={{
            alignItems: "center",
            height: "100%",
            justifyContent: "center",
            left: 0,
            position: "absolute",
            top: "0px",
            width: "100%"
          }}
        >
          {highlightSpans.map((ele) => {
            return ele;
          })}
        </div>
        {props1.annotationLayer.children}
        {props1.textLayer.children}
      </>
    );
  };

  useLayoutEffect(() => {
    if (state?.activePageNumber) {
      jumpToPage(state.activePageNumber - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.activePageNumber]);

  const reDownloadDoc = () => {
    setRedownload((prevState) => {
      return !prevState;
    });
  };

  if (!PDFData) {
    return (
      <div className="flex flex-col relative items-center justify-start overflow-x-auto w-full h-full bg-none rounded" style={{ msOverflowStyle: 'none' }}>
          <Button
            size="icon"
            variant="ghost"
            aria-label='Re-download'
            onClick={reDownloadDoc}
            className="absolute right-0 z-10"
          >
            <RefreshIcon color='#808080' width={16} height={16} />
          </Button>
        <CircularProgressWithLabel value={downloadProgress} theme={theme} />
      </div>
    );
  }
  return (
    <div 
      className="flex flex-col relative rounded justify-start overflow-x-auto items-start w-full h-full bg-none [&_.rpv-default-layout__container]:border-none [&_.rpv-default-layout__sidebar--ltr]:border-none [&_.rpv-default-layout__sidebar-headers]:bg-neutral-4 [&_.rpv-default-layout__sidebar-headers]:border-r [&_.rpv-default-layout__sidebar-headers]:border-neutral-5 [&_.rpv-default-layout__toolbar]:bg-neutral-4 [&_.rpv-default-layout__toolbar]:border [&_.rpv-default-layout__toolbar]:border-neutral-5 [&_.rpv-core__inner-pages--vertical]:top-[13px] [&_.rpv-default-layout__body]:overflow-hidden"
      style={{ msOverflowStyle: 'none' }}
      id='pdfContainer'
    >
      <Worker workerUrl='https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js'>
        <Viewer
          fileUrl={PDFData}
          plugins={[defaultLayoutPluginInstance, pageNavigationPluginInstance]}
          onDocumentLoad={onDocumentLoadSuccess}
          renderPage={onRenderPage}
        />
      </Worker>
    </div>
  );
};
PDFViewer.propTypes = {
  state: PropTypes.shape({
    activePageNumber: PropTypes.number,
    totalPages: PropTypes.number,
    isDocumentLoading: PropTypes.bool,
    path: PropTypes.string,
    documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    highlightCoordinates: PropTypes.arrayOf(
      PropTypes.shape({
        pageNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        topLeft: PropTypes.arrayOf(PropTypes.number),
        bottomRight: PropTypes.arrayOf(PropTypes.number),
      })
    ),
  }).isRequired,
  setState: PropTypes.func,
  cacheKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  url: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  useCache: PropTypes.bool,
  name: PropTypes.string,
  highlightChecked: PropTypes.bool,
  showHighlight: PropTypes.bool,
  highlightDisabled: PropTypes.bool,
  onChangeHighlightSelect: PropTypes.func,
  theme: PropTypes.string,
  reuploadFile: PropTypes.func,
  captureDocNotReq: PropTypes.bool,
};
